import type { ProblemDetails } from '@tt/shared';

export const API_BASE = '/api/v1';

/** Error thrown for non-2xx responses; carries the RFC 9457 problem details from the API. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.code = problem.code;
    this.problem = problem;
  }

  /** Field-level messages keyed by path, for forms. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem.errors ?? []) out[e.path] ??= e.message;
    return out;
  }
}

type Query = Record<string, string | number | boolean | string[] | undefined>;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Sent as-is instead of JSON — a file upload, where the body *is* the file. */
  rawBody?: Blob | ArrayBuffer;
  contentType?: string;
  query?: Query;
  signal?: AbortSignal;
}

function buildQuery(query?: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined) continue;
    if (Array.isArray(v)) v.forEach((item) => params.append(k, item));
    else params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/**
 * Thin fetch wrapper: same-origin cookies, the CSRF header on every request, JSON in/out,
 * problem details → ApiError.
 */
export async function api<T = void>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}${buildQuery(opts.query)}`, {
    method: opts.method ?? 'GET',
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'fetch',
      Accept: 'application/json',
      ...(opts.rawBody !== undefined
        ? { 'Content-Type': opts.contentType ?? 'application/octet-stream' }
        : opts.body !== undefined
          ? { 'Content-Type': 'application/json' }
          : {}),
    },
    body:
      opts.rawBody !== undefined
        ? opts.rawBody
        : opts.body !== undefined
          ? JSON.stringify(opts.body)
          : undefined,
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const problem =
      json && typeof json === 'object' && 'status' in json
        ? (json as ProblemDetails)
        : { type: 'about:blank', title: res.statusText, status: res.status, code: 'HTTP_ERROR' };
    throw new ApiError(problem);
  }
  return json as T;
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
